import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Locale } from '../constants';
import deleteAccount from './deleteAccount';

export const NAMESPACE = 'account';

type DeleteAccountParameters = Parameters<typeof deleteAccount>[0];

export type UserAttributes = {
  sub?: string; // Cognito accountId
  accountId?: string; // get accountId from backend API
  email_verified?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  gender?: string;
  locale?: string;
  'custom:a8_parameter_id'?: string; // A8 Affiliate Parameter ID for logged in and non logged in user
  'custom:a8_parameter_expiry'?: number; // A8 Affiliate Parameter expiry date in epoch for logged in and non logged in user
  // custom:email attributes are being used in Cognito's Pre token lambda trigger to revert the value when SSO replace email value
  'custom:email'?: string;
  // custom:given_name attributes are being used in Cognito's Pre token lambda trigger to revert the value when SSO replace family_name value
  'custom:given_name'?: string;
  // custom:family_name attributes are being used in Cognito's Pre token lambda trigger to revert the value when SSO replace family_name value
  'custom:family_name'?: string;
  'custom:given_name_kana'?: string;
  'custom:family_name_kana'?: string;
  'custom:phone'?: string; // using the built in phone_number attribute will use Cognito's phone number validation, which can block valid numbers
  'custom:country'?: string;
  'custom:postal_code'?: string;
  'custom:region'?: string;
  'custom:source'?: string;
  'custom:address'?: string; // the default address is some kind of object
};

export type Account = {
  userAttributes: UserAttributes;
};

export const deleteAccountThunk = createAsyncThunk<boolean, DeleteAccountParameters>(
  `${NAMESPACE}/deleteAccount`,
  async (body, { rejectWithValue }) => {
    try {
      await deleteAccount(body);
      return true;
    } catch (err) {
      console.error(err);
      return rejectWithValue('UNKNOWN_ERROR');
    }
  }
);

export const initialState = {
  userAttributes: {
    accountId: '',
    given_name: '',
    family_name: '',
    email: '',
    email_verified: false,
    loggedIn: false,
    gender: '',
    locale: Locale.JAPANESE,
    'custom:address': '',
    'custom:given_name_kana': '',
    'custom:family_name_kana': '',
    'custom:phone': '',
    'custom:country': '',
    'custom:postal_code': '',
    'custom:region': '',
  },
  userInitialized: false,
  deleteAccountLoading: false,
  deleteAccountFailed: false,
};

const accountSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    setUserAttributes(state, action: PayloadAction<UserAttributes>) {
      state.userAttributes.accountId = action.payload.accountId || '';
      state.userAttributes.given_name = action.payload.given_name || '';
      state.userAttributes.family_name = action.payload.family_name || '';
      state.userAttributes.email = action.payload.email || '';
      state.userAttributes.email_verified = action.payload.email_verified === 'true';
      state.userAttributes.gender = action.payload.gender || '';
      state.userAttributes.locale = action.payload.locale || Locale.JAPANESE;
      state.userAttributes['custom:family_name_kana'] =
        action.payload['custom:family_name_kana'] || '';
      state.userAttributes['custom:phone'] = action.payload['custom:phone'] || '';
      state.userAttributes['custom:country'] = action.payload['custom:country'] || '';
      state.userAttributes['custom:postal_code'] = action.payload['custom:postal_code'] || '';
      state.userAttributes['custom:region'] = action.payload['custom:region'] || '';
      state.userAttributes['custom:address'] = action.payload['custom:address'] || '';
    },
    clearUserAttributes(state) {
      state.userAttributes.accountId = '';
      state.userAttributes.given_name = '';
      state.userAttributes.family_name = '';
      state.userAttributes.email = '';
      state.userAttributes.email_verified = false;
      state.userAttributes.loggedIn = false;
      state.userAttributes.gender = '';
      state.userAttributes['custom:given_name_kana'] = '';
      state.userAttributes['custom:family_name_kana'] = '';
      state.userAttributes['custom:phone'] = '';
      state.userAttributes['custom:country'] = '';
      state.userAttributes['custom:postal_code'] = '';
      state.userAttributes['custom:region'] = '';
      state.userAttributes['custom:address'] = '';
    },
    setEmail(state, action: PayloadAction<Pick<UserAttributes, 'email' | 'email_verified'>>) {
      state.userAttributes.email = action.payload.email || '';
      state.userAttributes.email_verified = action.payload.email_verified === 'true';
    },
    setLoggedIn(state) {
      state.userAttributes.loggedIn = true;
    },
    setUserInitialized(state) {
      state.userInitialized = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteAccountThunk.pending, (state) => {
      state.deleteAccountLoading = true;
    });
    builder.addCase(deleteAccountThunk.fulfilled, (state) => {
      state.deleteAccountFailed = false;
      state.deleteAccountLoading = false;
    });
    builder.addCase(deleteAccountThunk.rejected, (state) => {
      state.deleteAccountFailed = true;
      state.deleteAccountLoading = false;
    });
  },
});

export default accountSlice;
